import { TextFieldModule } from '@angular/cdk/text-field'
import { DatePipe, NgTemplateOutlet } from '@angular/common'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Router, RouterLink } from '@angular/router'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  EMPTY,
  Observable,
  Subject,
  ReplaySubject,
  catchError,
  concatMap,
  defer,
  exhaustMap,
  finalize,
  filter,
  forkJoin,
  of,
  take,
  interval,
  takeUntil,
  tap,
  timer,
  timeout,
  TimeoutError,
  throwError,
} from 'rxjs'

import {
  KpiAiDraftStatus,
  KpiFeedbackChoiceCategory,
  KpiNextActionInput,
  KpiNextActionResultChoice,
  KpiOperatorFeedbackPoints,
  KpiResponseStatus,
  UpdateKpiFeedbackSheetAssessmentInput,
  UpdateKpiFeedbackSheetInput,
} from '../../../../_generated/graphql'
import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import {
  KPI_DASHBOARD_NOT_FOUND_MESSAGE,
  KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE,
  KPI_SHEET_NOT_FOUND_MESSAGE,
  KPI_SHEET_ASSESSMENT_NOT_FOUND_MESSAGE,
  KPI_NEXT_ACTION_NOT_FOUND_MESSAGE,
  KPI_PREVIOUS_SHEET_MESSAGES,
  KPI_LATEST_SHEET_ONLY_MESSAGE,
  KPI_AI_DRAFT_PENDING_MESSAGE,
  KPI_ASSESSMENTS_UNCHANGED_MESSAGE,
  KpiDashboardDefinition,
  KpiDashboardService,
  KpiFeedbackTargetNotFoundError,
  KpiFeedbackChoices,
  KpiFeedbackSheetAssessmentView,
  KpiFeedbackSheetView,
  KpiFeedbackSheets,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { captureException } from '../../../util/captureException'
import {
  CustomError,
  CustomErrorCode,
  isKpiCustomError,
} from '../../../util/throwIfCustomError'
import { handleDashboardNotFound } from '../handle-dashboard-not-found'
import {
  EditableFeedbackPointsValue,
  FeedbackAssessmentChangePreservation,
  FeedbackAssessmentEditableField,
  FeedbackSaveField,
  FeedbackSheetEditableField,
  KPI_NEXT_ACTION_RESULT_CHOICES,
  KeyActionScoreRow,
  OperatorFeedbackAssessmentEditableField,
  PreviousNextActionRow,
  applyPreviousNextActionResult,
  buildAnswerablePreviousNextActionRows,
  buildAssessmentLinkLabel,
  buildFeedbackAssessmentUpdateInput,
  buildFeedbackSheetUpdateInput,
  buildKeyActionScoreRows,
  buildNextActionsUpdateInput,
  buildOperatorFeedbackPointsUpdateInput,
  buildPreviousNextActionResultInput,
  buildPreviousNextActionRows,
  calculateKeyActionAverage,
  filterFeedbackChoices,
  formatDateInputValue,
  formatFeedbackDate,
  formatFeedbackMeetingTitle,
  formatFeedbackOverallScore,
  formatNextActionStatus,
  getFeedbackAssessmentChangePreservation,
  isManagerOnlyAssessmentField,
  isManagerOnlySheetField,
  isPreviousNextActionReopened,
  normalizeNullableText,
  resolveNextActionNoteState,
} from './feedback-sheet.logic'
import {
  NewFeedbackSheetDialogComponent,
  NewFeedbackSheetDialogData,
  NewFeedbackSheetDialogResult,
} from './new-feedback-sheet-dialog.component'

export interface KpiFeedbackSheetViewer {
  userId: string
  canManage: boolean
}

type NextActionForm = FormGroup<{
  id: FormControl<string>
  text: FormControl<string>
}>

type FeedbackChoiceForm = FormGroup<{
  choiceId: FormControl<string>
  selected: FormControl<boolean>
}>

type OperatorFeedbackPointsForm = FormGroup<{
  choices: FormArray<FeedbackChoiceForm>
  freeText: FormControl<string>
}>

type FeedbackAssessmentForm = FormGroup<{
  operatorGoodPoints: OperatorFeedbackPointsForm
  operatorImprovementPoints: OperatorFeedbackPointsForm
  goodPoints: FormControl<string>
  improvementPoints: FormControl<string>
  nextActions: FormArray<NextActionForm>
}>

type PreviousResultForm = FormGroup<{
  choice: FormControl<KpiNextActionResultChoice | null>
  note: FormControl<string>
}>

interface QueuedFeedbackSave {
  loadRequestId: number
  failureKey: string
  sheetId: string
  operationFactory: () => Observable<unknown>
  applyPersistedValue: (
    sheet: KpiFeedbackSheetView,
    result: unknown,
  ) => KpiFeedbackSheetView
  affectedAssessmentId: string | null
  affectedField: FeedbackSaveField
}

interface FeedbackSaveState {
  confirmedPersistedSheet: KpiFeedbackSheetView | null
  persistedSheet: KpiFeedbackSheetView | null
  readonly queue: Subject<QueuedFeedbackSave>
  readonly pendingSaves: QueuedFeedbackSave[]
  pendingSaveCount: number
  sheetLoadRequestId: number
  isDestroyed: boolean
}

interface FeedbackAssessmentViewModel {
  assessment: KpiFeedbackSheetAssessmentView
  form: FeedbackAssessmentForm
  assessDateText: string
  meetingTitleText: string
  assessmentLinkParams: { 'user-id': string; 'assessment-id': string }
  assessmentLinkLabel: string
  overallScoreText: string
  keyActionAverageText: string
  keyActionScoreRows: KeyActionScoreRow[]
  nextActionResultTexts: Readonly<Record<string, string | null>>
}

type AiDraftSheetField = 'managerHypothesis' | 'otherNotes'
type AiDraftAssessmentField = 'goodPoints' | 'improvementPoints' | 'nextActions'
type AiDraftEditableField = AiDraftSheetField | AiDraftAssessmentField

/** 対象変更・再読み込み時の保存待機を 30 秒で打ち切り、操作不能を防ぐ。 */
const FEEDBACK_SAVE_TIMEOUT_MS = 30_000

const AI_DRAFT_POLLING_INTERVAL_MS = 5_000
const AI_DRAFT_POLLING_TIMEOUT_MS = 5 * 60 * 1_000

@UntilDestroy()
@Component({
  selector: 'app-kpi-feedback-sheet-section',
  standalone: true,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    MatIconModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    RouterLink,
    TextFieldModule,
  ],
  templateUrl: './kpi-feedback-sheet-section.component.html',
  styleUrl: './kpi-feedback-sheet-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiFeedbackSheetSectionComponent implements OnInit, OnDestroy {
  @Input({ required: true }) dashboardId!: string
  @Input({ required: true }) operatorUserId!: string
  @Input({ required: true }) viewer!: KpiFeedbackSheetViewer
  @Input({ required: true })
  keyActions!: KpiDashboardDefinition['keyActions']

  isLoading = true
  feedbackChoices: KpiFeedbackChoices = []
  operatorGoodPointChoices: KpiFeedbackChoices = []
  operatorImprovementPointChoices: KpiFeedbackChoices = []
  feedbackSheets: KpiFeedbackSheets = []
  selectedSheet: KpiFeedbackSheetView | null = null
  selectedSheetId: string | null = null
  previousNextActionRows: PreviousNextActionRow[] = []
  readonly nextActionResultChoices = KPI_NEXT_ACTION_RESULT_CHOICES
  readonly previousResultForms = new Map<string, PreviousResultForm>()
  private isConfirmingResponse = false
  private readonly failedFeedbackSaves = new Map<string, Set<string>>()
  isCompletingResponse = false
  private readonly pendingPreviousNextActionIds = new Set<string>()
  feedbackAssessmentViewModels: FeedbackAssessmentViewModel[] = []
  isSheetLoading = false
  isDeletingSheet = false
  isChangingAssessments = false
  private readonly activeFeedbackSaves = new Set<QueuedFeedbackSave>()
  private readonly discardedFeedbackSaves = new WeakSet<QueuedFeedbackSave>()
  /** シート復旧の一覧・詳細再取得中は追加の通知と再取得を抑止する。 */
  private isRecoveringSheet = false
  /** 復旧先のシートも消失した場合の一覧再取得は一度だけ許可する。 */
  private recoveryRetried = false
  private isRecoveringChangedAssessments = false
  private changedAssessmentDetailsReady$: ReplaySubject<boolean> | null = null
  private readonly feedbackSaveFinished$ = new Subject<void>()
  feedbackErrorMessage = ''
  feedbackSaveErrorMessage = ''
  feedbackSaveStatus: 'idle' | 'saving' | 'saved' = 'idle'
  isAiDraftPollingTimedOut = false
  isAiDraftReloading = false
  aiDraftReloadErrorMessage = ''

  readonly sheetForm = new FormGroup({
    interviewDate: new FormControl('', { nonNullable: true }),
    consultationTopics: new FormControl('', { nonNullable: true }),
    operatorSelfIssues: new FormControl('', { nonNullable: true }),
    managerHypothesis: new FormControl('', { nonNullable: true }),
    otherNotes: new FormControl('', { nonNullable: true }),
  })

  private readonly assessmentForms = new Map<string, FeedbackAssessmentForm>()
  private readonly aiDraftEditingFields = new Set<string>()
  private readonly stopAiDraftPolling$ = new Subject<void>()
  private aiDraftPollingRunId = 0
  /** pendingSaveCount は常に pendingSaves.length と一致する。 */
  private readonly saveState: FeedbackSaveState = {
    confirmedPersistedSheet: null,
    persistedSheet: null,
    queue: new Subject<QueuedFeedbackSave>(),
    pendingSaves: [],
    pendingSaveCount: 0,
    sheetLoadRequestId: 0,
    isDestroyed: false,
  }

  @ViewChildren('feedbackSheetTab')
  private feedbackSheetTabs!: QueryList<ElementRef<HTMLButtonElement>>

  @ViewChild('feedbackShell', { static: true })
  private feedbackShell!: ElementRef<HTMLElement>

  @ViewChild('sheetPanel')
  private sheetPanel?: ElementRef<HTMLElement>

  private stickyHead?: HTMLElement
  private shouldAlignSheetAfterLoad = false

  /** 直前に観測した固定領域の高さ。0 から正の値への変化を再表示とみなす。 */
  private lastStickyHeadHeight = 0
  // ResizeObserver は zone.js のパッチ対象外。Angular の外で DOM だけを操作する。
  private readonly stickyHeightObserver = new ResizeObserver(() => {
    if (this.stickyHead) {
      const height = this.stickyHead.offsetHeight
      const wasHidden = this.lastStickyHeadHeight === 0
      this.lastStickyHeadHeight = height
      this.feedbackShell.nativeElement.style.setProperty(
        '--feedback-head-height',
        `${height}px`,
      )
      if (wasHidden && height > 0) {
        this.revealSelectedTab()
      }
    }
  })

  /** 一覧の読み込みに伴う固定領域の生成・破棄に合わせて高さの監視を切り替える。 */
  @ViewChild('feedbackStickyHead')
  private set feedbackStickyHead(head: ElementRef<HTMLElement> | undefined) {
    if (this.stickyHead) {
      this.stickyHeightObserver.unobserve(this.stickyHead)
    }
    this.feedbackShell.nativeElement.style.removeProperty(
      '--feedback-head-height',
    )
    this.lastStickyHeadHeight = 0
    this.stickyHead = head?.nativeElement
    if (this.stickyHead) {
      this.stickyHeightObserver.observe(this.stickyHead, { box: 'border-box' })
    }
  }

  constructor(
    private kpiDashboardService: KpiDashboardService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private messageDialogService: MessageDialogService,
    private changeDetectorRef: ChangeDetectorRef,
    private injector: Injector,
    private router: Router,
  ) {}

  /** 一覧・選択肢と自動保存キューを初期化する。 */
  ngOnInit(): void {
    if (this.dashboardId === '' || this.operatorUserId === '') {
      throw new Error('Feedback sheet section input is missing')
    }
    this.initializeFeedbackSaveQueue()
    this.load()
  }

  /**
   * 新規の自動保存要求を止め、投入済み要求は concatMap の完了待ちで最後まで実行する。
   * Subject の complete 後に購読も自然終了するため、手動 unsubscribe は不要。
   */
  ngOnDestroy(): void {
    this.stickyHeightObserver.disconnect()
    this.saveState.isDestroyed = true
    this.finishSheetRecovery()
    this.stopAiDraftPolling()
    this.stopAiDraftPolling$.complete()
    this.saveState.queue.complete()
  }

  retry(): void {
    this.load()
  }

  get canCreateSheet(): boolean {
    return this.viewer.userId === this.operatorUserId || this.viewer.canManage
  }

  /** 固定中の切替では、詳細読み込み中もページの高さを確保する。 */
  get shouldPreserveSheetLoadingHeight(): boolean {
    return this.shouldAlignSheetAfterLoad
  }

  /** 記入完了を操作できる本人かを返す。 */
  get isOperatorSelf(): boolean {
    return this.viewer.userId === this.operatorUserId
  }

  get canDeleteSheet(): boolean {
    return this.viewer.canManage
  }

  /** 管理ロールが最新回の対象を変更できるか、停滞した草案の復旧も含めて返す。 */
  get canChangeAssessments(): boolean {
    const sheet = this.selectedSheet
    return (
      this.canDeleteSheet &&
      sheet != null &&
      (sheet.aiDraftStatus !== KpiAiDraftStatus.Pending ||
        sheet.isAiDraftStale) &&
      this.feedbackSheets.some(({ id }) => id === sheet.id) &&
      this.feedbackSheets.every(
        ({ sheetNumber }) => sheetNumber <= sheet.sheetNumber,
      )
    )
  }

  /** 対象変更中を除き、ユーザーが選択したタブの詳細を取得する。 */
  selectFeedbackSheet(
    sheetId: string,
    wasSticky = this.isFeedbackHeadSticky(),
  ): void {
    if (this.isChangingAssessments) return
    this.loadFeedbackSheet(sheetId, wasSticky)
  }

  /** 内部の再取得にも使うシート詳細の読み込み処理。 */
  private loadFeedbackSheet(
    sheetId: string,
    wasSticky = this.isFeedbackHeadSticky(),
    feedbackErrorMessage = '',
    isRecovery = false,
  ): void {
    if (this.isSheetLoading && this.selectedSheetId === sheetId) {
      if (isRecovery) this.finishSheetRecovery()
      return
    }
    if (
      this.selectedSheetId === sheetId &&
      this.selectedSheet != null &&
      this.selectedSheet.id === sheetId
    ) {
      this.revealSelectedTabAfterRender(this.saveState.sheetLoadRequestId)
      if (isRecovery) this.finishSheetRecovery()
      return
    }

    this.shouldAlignSheetAfterLoad = wasSticky
    this.stopAiDraftPolling()
    this.isCompletingResponse = false
    this.isConfirmingResponse = false
    this.previousResultForms.clear()
    this.selectedSheetId = sheetId
    this.selectedSheet = null
    this.saveState.confirmedPersistedSheet = null
    this.saveState.persistedSheet = null
    this.previousNextActionRows = []
    this.feedbackAssessmentViewModels = []
    this.assessmentForms.clear()
    this.isSheetLoading = true
    this.feedbackErrorMessage = feedbackErrorMessage
    this.feedbackSaveErrorMessage = ''
    this.failedFeedbackSaves.delete(sheetId)
    this.feedbackSaveStatus = 'idle'
    this.isAiDraftPollingTimedOut = false
    this.isAiDraftReloading = false
    this.aiDraftReloadErrorMessage = ''
    this.aiDraftEditingFields.clear()
    this.changeDetectorRef.markForCheck()
    const requestId = ++this.saveState.sheetLoadRequestId
    let retryStarted = false
    this.revealSelectedTabAfterRender(requestId)

    this.kpiDashboardService
      .feedbackSheet(sheetId)
      .pipe(
        finalize(() => {
          if (isRecovery && !retryStarted) this.finishSheetRecovery()
          if (requestId === this.saveState.sheetLoadRequestId) {
            this.isSheetLoading = false
            this.changeDetectorRef.markForCheck()
            if (
              !this.saveState.isDestroyed &&
              this.selectedSheet != null &&
              !this.feedbackErrorMessage
            ) {
              afterNextRender(
                () => {
                  if (requestId === this.saveState.sheetLoadRequestId) {
                    this.alignLoadedSheetStart()
                  }
                },
                { injector: this.injector },
              )
            }
          }
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (sheet) => {
          if (
            requestId !== this.saveState.sheetLoadRequestId ||
            sheet.id !== this.selectedSheetId
          ) {
            return
          }
          this.selectedSheet = sheet
          this.saveState.confirmedPersistedSheet = sheet
          this.rebuildPersistedSheet()
          this.syncPreviousResultForms(sheet)
          this.rebuildPreviousNextActionRows(sheet)
          this.initializeFeedbackForms(sheet)
          this.initializeFeedbackAssessmentViewModels()
          // フォーム値の反映後に textarea の自動伸縮（cdkTextareaAutosize）が高さを測り直せるよう、
          // finalize の markForCheck に依存せず明示的にビューを更新対象にする
          this.changeDetectorRef.markForCheck()
          if (sheet.aiDraftStatus === KpiAiDraftStatus.Pending) {
            this.startAiDraftPolling()
          }
        },
        error: (error: unknown) => {
          if (requestId !== this.saveState.sheetLoadRequestId) {
            return
          }
          if (isRecovery) {
            if (this.isDeletedSheetError(error) && !this.recoveryRetried) {
              this.recoveryRetried = true
              retryStarted = true
              this.clearSelectedFeedbackSheet()
              this.load(null, true)
              return
            }
          } else if (this.recoverMissingSheet(error, sheetId)) return
          if (handleDashboardNotFound(error, this.router, this.snackBar)) return
          if (!this.isExpectedKpiError(error)) captureException(error)
          this.shouldAlignSheetAfterLoad = false
          this.feedbackErrorMessage =
            'フィードバックシートの読み込みに失敗しました。'
        },
      })
  }

  /** 描画後、現在の選択タブを横スクロールの表示範囲へ寄せる。 */
  private revealSelectedTabAfterRender(requestId: number): void {
    afterNextRender(
      () => {
        if (requestId !== this.saveState.sheetLoadRequestId || this.isLoading) {
          return
        }
        this.revealSelectedTab()
      },
      { injector: this.injector },
    )
  }

  /** 選択中タブのはみ出し分だけ、タブ列を横方向にスクロールする。 */
  private revealSelectedTab(): void {
    const tab = this.feedbackSheetTabs.find(
      ({ nativeElement }) =>
        nativeElement.getAttribute('aria-selected') === 'true',
    )?.nativeElement
    if (!tab) {
      return
    }
    const tabs = tab.parentElement
    if (!tabs) {
      throw new Error('Feedback sheet tab list is missing')
    }
    if (tabs.clientWidth === 0) {
      return
    }
    if (tab.offsetLeft < tabs.scrollLeft) {
      tabs.scrollLeft = tab.offsetLeft
    } else if (
      tab.offsetLeft + tab.offsetWidth >
      tabs.scrollLeft + tabs.clientWidth
    ) {
      tabs.scrollLeft = tab.offsetLeft + tab.offsetWidth - tabs.clientWidth
    }
  }

  /** 表示中の固定領域がセクションの自然位置を通過したかを返す。 */
  private isFeedbackHeadSticky(): boolean {
    if (
      !this.stickyHead ||
      this.stickyHead.offsetHeight === 0 ||
      getComputedStyle(this.stickyHead).position !== 'sticky'
    ) {
      return false
    }
    const shell = this.feedbackShell.nativeElement
    return shell.getBoundingClientRect().top + shell.clientTop < 0
  }

  /** 切替前に固定中だった場合、読み込み済み本文の描画後に先頭位置を揃える。 */
  private alignLoadedSheetStart(): void {
    const shouldAlign = this.shouldAlignSheetAfterLoad
    this.shouldAlignSheetAfterLoad = false
    if (
      !shouldAlign ||
      !this.stickyHead ||
      !this.sheetPanel ||
      this.stickyHead.offsetHeight === 0 ||
      getComputedStyle(this.stickyHead).position !== 'sticky'
    ) {
      return
    }
    const offset =
      this.sheetPanel.nativeElement.getBoundingClientRect().top -
      this.stickyHead.offsetHeight
    if (offset !== 0) {
      window.scrollTo({ top: window.scrollY + offset })
    }
  }

  /** 左右矢印キーで隣接するシートタブを選択してフォーカスする。 */
  moveFeedbackSheetTab(event: KeyboardEvent, sheetId: string): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return
    }
    const currentIndex = this.feedbackSheets.findIndex(
      (sheet) => sheet.id === sheetId,
    )
    if (currentIndex === -1) {
      throw new Error(`Feedback sheet tab is missing: ${sheetId}`)
    }
    event.preventDefault()
    const offset = event.key === 'ArrowRight' ? 1 : -1
    const nextIndex =
      (currentIndex + offset + this.feedbackSheets.length) %
      this.feedbackSheets.length
    const nextSheet = this.feedbackSheets[nextIndex]
    const nextTab = this.feedbackSheetTabs.get(nextIndex)
    if (nextSheet == null || nextTab == null) {
      throw new Error(`Feedback sheet tab index is invalid: ${nextIndex}`)
    }
    this.selectFeedbackSheet(nextSheet.id)
    nextTab.nativeElement.focus()
  }

  /** 新規作成モーダルを開き、作成したシートのタブを選択する。 */
  openNewFeedbackSheetDialog(): void {
    if (!this.canCreateSheet || this.isChangingAssessments) {
      return
    }

    this.matDialog
      .open<
        NewFeedbackSheetDialogComponent,
        NewFeedbackSheetDialogData,
        NewFeedbackSheetDialogResult
      >(NewFeedbackSheetDialogComponent, {
        data: {
          dashboardId: this.dashboardId,
          userId: this.operatorUserId,
        },
        width: '920px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 32px)',
        autoFocus: false,
        panelClass: 'feedback-sheet-dialog',
      })
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result?.mode === 'missing') {
          if (
            this.selectedSheetId != null &&
            this.recoverMissingSheet(result.error, this.selectedSheetId)
          )
            return
          if (handleDashboardNotFound(result.error, this.router, this.snackBar))
            return
          this.feedbackErrorMessage = isKpiCustomError(
            result.error,
            CustomErrorCode.NOT_FOUND,
            [KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE],
          )
            ? 'このオペレーターはダッシュボードのメンバーから外れています。'
            : 'フィードバックシートの読み込みに失敗しました。'
          this.changeDetectorRef.markForCheck()
          return
        }
        if (result == null) {
          return
        }
        this.load(result.sheetId)
      })
  }

  /** 現在の対象を選択した変更モーダルを開き、初期化の了承後に変更する。 */
  openChangeFeedbackSheetDialog(): void {
    const sheet = this.selectedSheet
    if (
      !this.canChangeAssessments ||
      this.isSheetLoading ||
      sheet == null ||
      this.isChangingAssessments
    )
      return
    if (this.failedFeedbackSaves.get(sheet.id)?.size) {
      this.feedbackErrorMessage =
        '保存できていない入力があるため、対象アセスメントを変更できませんでした。'
      this.changeDetectorRef.markForCheck()
      return
    }
    const requestId = this.saveState.sheetLoadRequestId
    this.isChangingAssessments = true
    this.changeDetectorRef.markForCheck()
    this.selectChangedFeedbackTargets(sheet)
      .pipe(
        concatMap((assessmentIds) =>
          this.flushFeedbackSavesBeforeChange(sheet.id).pipe(
            concatMap(() =>
              this.kpiDashboardService.changeFeedbackSheetAssessments(
                sheet.id,
                assessmentIds,
              ),
            ),
          ),
        ),
        finalize(() => {
          this.isChangingAssessments = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (sheetId) => {
          // ブロック ID が変わるため、草案の差分マージを使わず詳細を置き換える。
          this.clearSelectedFeedbackSheet()
          this.load(sheetId)
        },
        error: (error: unknown) => {
          if (this.recoverMissingSheet(error, sheet.id)) return
          if (error instanceof UnresolvedFeedbackSaveError) {
            if (
              this.isRecoveringSheet ||
              requestId !== this.saveState.sheetLoadRequestId
            )
              return
            this.isSheetLoading = false
            this.feedbackErrorMessage =
              '保存できていない入力があるため、対象アセスメントを変更できませんでした。'
            return
          }
          if (handleDashboardNotFound(error, this.router, this.snackBar)) return
          const isExpectedAssessmentChangeError = isKpiCustomError(
            error,
            CustomErrorCode.BAD_REQUEST,
            [
              KPI_LATEST_SHEET_ONLY_MESSAGE,
              KPI_AI_DRAFT_PENDING_MESSAGE,
              KPI_ASSESSMENTS_UNCHANGED_MESSAGE,
            ],
          )
          if (
            !this.isExpectedKpiError(error) &&
            !isExpectedAssessmentChangeError
          ) {
            captureException(error)
          }
          // 通信エラーでもサーバー側は変更済みの場合があるため、詳細を取り直す。
          const message =
            error instanceof CustomError &&
            error.code !== CustomErrorCode.NOT_FOUND &&
            (error.code !== CustomErrorCode.BAD_REQUEST ||
              isExpectedAssessmentChangeError)
              ? error.message
              : '対象アセスメントを変更できませんでした。再読み込みして再度お試しください。'
          this.clearSelectedFeedbackSheet()
          this.isSheetLoading = false
          this.loadFeedbackSheet(sheet.id, false, message)
        },
      })
  }

  /** 確認をキャンセルしたら選び直した内容で選択ダイアログへ戻る。 */
  private selectChangedFeedbackTargets(
    sheet: KpiFeedbackSheetView,
    initialAssessmentIds?: string[],
  ): Observable<string[]> {
    return this.matDialog
      .open<
        NewFeedbackSheetDialogComponent,
        NewFeedbackSheetDialogData,
        NewFeedbackSheetDialogResult
      >(NewFeedbackSheetDialogComponent, {
        data: {
          dashboardId: this.dashboardId,
          userId: this.operatorUserId,
          sheetId: sheet.id,
          assessmentIds: sheet.assessments.map(
            ({ assessmentId }) => assessmentId,
          ),
          initialAssessmentIds,
          isAiDraftFailed: sheet.aiDraftStatus === KpiAiDraftStatus.Failed,
          isAiDraftStale: sheet.isAiDraftStale,
        },
        width: '920px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 32px)',
        autoFocus: false,
        panelClass: 'feedback-sheet-dialog',
      })
      .afterClosed()
      .pipe(
        concatMap((result) => {
          if (result?.mode === 'missing') {
            if (this.recoverMissingSheet(result.error, sheet.id)) return EMPTY
            if (
              handleDashboardNotFound(result.error, this.router, this.snackBar)
            )
              return EMPTY
            this.feedbackErrorMessage = isKpiCustomError(
              result.error,
              CustomErrorCode.NOT_FOUND,
              [KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE],
            )
              ? 'このオペレーターはダッシュボードのメンバーから外れています。'
              : '対象アセスメントを変更できませんでした。再読み込みして再度お試しください。'
            this.changeDetectorRef.markForCheck()
            return EMPTY
          }
          if (result == null || result.mode !== 'change') return EMPTY
          return this.messageDialogService
            .showConfirm(
              this.buildAssessmentChangeWarning(sheet, result.assessmentIds),
              {
                title: '対象アセスメント変更の確認',
                primaryButtonText: '変更する',
                secondaryButtonText: 'キャンセル',
              },
            )
            .pipe(
              concatMap((answer) =>
                answer === DialogResult.PrimaryButtonClicked
                  ? of(result.assessmentIds)
                  : this.selectChangedFeedbackTargets(
                      sheet,
                      result.assessmentIds,
                    ),
              ),
            )
        }),
      )
  }

  /** 選択差分に応じて、消える対象と残る記入内容を具体的に案内する。 */
  private buildAssessmentChangeWarning(
    sheet: KpiFeedbackSheetView,
    assessmentIds: string[],
  ): string {
    const kept = sheet.assessments.filter(({ assessmentId }) =>
      assessmentIds.includes(assessmentId),
    )
    const removed = sheet.assessments.filter(
      ({ assessmentId }) => !assessmentIds.includes(assessmentId),
    )
    const added = assessmentIds.some(
      (id) =>
        !sheet.assessments.some(({ assessmentId }) => assessmentId === id),
    )
    /** 確認文の箇条書きで対象を特定するための表示名を返す。 */
    const describe = (assessment: KpiFeedbackSheetAssessmentView): string =>
      `・${formatFeedbackDate(assessment.assessDate)}「${formatFeedbackMeetingTitle(assessment.meetingTitle)}」の記入内容`
    const removedLines = removed.map(describe)
    if (kept.length === 0) removedLines.push('・主任側の課題仮説・特記事項')
    const keptLines = [
      ...kept.map(describe),
      '・相談事項・自身の課題感・面談実施日',
    ]
    const otherLines = [
      ...(added ? ['・オペレーターの記入状態を未記入に戻します'] : []),
      '・AI 草案を空欄部分に作り直します',
    ]
    return [
      ...(removedLines.length > 0
        ? ['【消える内容】', ...removedLines, '']
        : []),
      '【残る内容】',
      ...keptLines,
      '',
      '【その他】',
      ...otherLines,
      '',
      '変更しますか？',
    ].join('\n')
  }

  /** 未 blur の編集をキューへ積む。別画面変更後は維持欄と残るブロックだけを対象にする。 */
  private enqueueUnsavedFeedbackChanges(
    preservation: FeedbackAssessmentChangePreservation | null,
  ): void {
    const requestId = this.saveState.sheetLoadRequestId
    const sheetFields = Object.keys(
      this.sheetForm.controls,
    ) as FeedbackSheetEditableField[]
    for (const field of sheetFields) {
      if (requestId !== this.saveState.sheetLoadRequestId) return
      if (
        (preservation == null || preservation.preservedFields.has(field)) &&
        this.sheetForm.controls[field].dirty &&
        (this.viewer.canManage || !isManagerOnlySheetField(field))
      ) {
        this.saveFeedbackSheetField(field)
      }
    }
    const assessmentFields: FeedbackAssessmentEditableField[] = [
      'operatorGoodPoints',
      'operatorImprovementPoints',
      'goodPoints',
      'improvementPoints',
    ]
    for (const [assessmentId, form] of this.assessmentForms) {
      if (preservation != null && !preservation.keptBlockIds.has(assessmentId))
        continue
      for (const field of assessmentFields) {
        if (requestId !== this.saveState.sheetLoadRequestId) return
        if (
          form.controls[field].dirty &&
          (this.viewer.canManage || !isManagerOnlyAssessmentField(field))
        ) {
          this.saveFeedbackAssessmentField(assessmentId, field)
        }
      }
      if (requestId !== this.saveState.sheetLoadRequestId) return
      if (this.viewer.canManage && form.controls.nextActions.dirty) {
        this.saveNextActions(assessmentId)
      }
    }
    if (requestId !== this.saveState.sheetLoadRequestId) return
    if (this.canEditPreviousResults) {
      for (const [actionId, form] of this.previousResultForms) {
        if (form.dirty) this.savePreviousNextActionResult(actionId)
      }
    }
  }

  /** 指定範囲の保存完了を待ち、失敗またはタイムアウトで変更・再読込を止める。 */
  private flushFeedbackSavesBeforeChange(
    sheetId: string,
    preservation: FeedbackAssessmentChangePreservation | null = null,
  ): Observable<void> {
    return defer(() => {
      const requestId = this.saveState.sheetLoadRequestId
      this.stopAiDraftPolling()
      this.isSheetLoading = true
      this.changeDetectorRef.markForCheck()
      this.enqueueUnsavedFeedbackChanges(preservation)
      const completed =
        this.saveState.pendingSaveCount === 0 &&
        this.activeFeedbackSaves.size === 0
          ? of(undefined)
          : this.feedbackSaveFinished$.pipe(
              filter(
                () =>
                  this.saveState.pendingSaveCount === 0 &&
                  this.activeFeedbackSaves.size === 0,
              ),
              take(1),
              timeout({
                first: FEEDBACK_SAVE_TIMEOUT_MS,
                with: () => throwError(() => new UnresolvedFeedbackSaveError()),
              }),
            )
      return completed.pipe(
        tap(() => {
          if (
            requestId !== this.saveState.sheetLoadRequestId ||
            (preservation == null && this.isRecoveringChangedAssessments) ||
            this.failedFeedbackSaves.get(sheetId)?.size
          )
            throw new UnresolvedFeedbackSaveError()
        }),
      )
    })
  }

  /** 管理ロール向けの確認ダイアログを開き、選択中シートを削除する。 */
  deleteSelectedFeedbackSheet(): void {
    if (
      !this.canDeleteSheet ||
      this.selectedSheet == null ||
      this.isDeletingSheet ||
      this.isChangingAssessments
    ) {
      return
    }

    const sheet = this.selectedSheet
    this.messageDialogService
      .showConfirm(
        `第${sheet.sheetNumber}回フィードバックシートを削除します。削除したシートは元に戻せません。`,
        {
          title: 'フィードバックシート削除の確認',
          primaryButtonText: '削除する',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(untilDestroyed(this))
      .subscribe((dialogResult) => {
        if (dialogResult !== DialogResult.PrimaryButtonClicked) {
          return
        }
        this.executeFeedbackSheetDelete(sheet.id)
      })
  }

  /** 同じ前回アクションのクローズ・再開が処理中かを返す。 */
  isPreviousNextActionUpdating(row: PreviousNextActionRow): boolean {
    return this.pendingPreviousNextActionIds.has(row.key)
  }

  /** 前回アクションの状態だけを更新し、編集中のフォームと保存キューを維持する。 */
  setPreviousNextActionClosed(
    row: PreviousNextActionRow,
    closed: boolean,
  ): void {
    if (!this.viewer.canManage || this.isPreviousNextActionUpdating(row)) {
      return
    }
    const sheetId = this.requirePersistedSheet().id
    const requestId = this.saveState.sheetLoadRequestId
    const actionKey = row.key
    this.pendingPreviousNextActionIds.add(actionKey)
    this.feedbackErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .setFeedbackNextActionClosed(
        row.sheetAssessmentId,
        row.nextActionId,
        closed,
      )
      .pipe(
        tap((updated) => {
          if (
            requestId !== this.saveState.sheetLoadRequestId ||
            sheetId !== this.selectedSheetId
          ) {
            return
          }
          const confirmedSheet = this.saveState.confirmedPersistedSheet
          if (confirmedSheet == null) {
            throw new PreviousNextActionOutOfSyncError(
              'Feedback sheet is missing',
            )
          }
          const closedAt = updated.closedAt
          this.saveState.confirmedPersistedSheet =
            applyPreviousNextActionClosed(confirmedSheet, row, closedAt)
          this.rebuildPersistedSheet()
          this.selectedSheet = this.saveState.confirmedPersistedSheet
          this.syncPreviousResultForms(this.selectedSheet)
          this.rebuildPreviousNextActionRows(this.selectedSheet)
          this.snackBar.open(
            closed
              ? 'ネクストアクションを完了にしました。'
              : 'ネクストアクションを再開しました。',
            '閉じる',
            { duration: 3000 },
          )
        }),
        finalize(() => {
          this.pendingPreviousNextActionIds.delete(actionKey)
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        error: (error: unknown) => {
          if (this.recoverMissingSheet(error, sheetId)) return
          captureException(error)
          if (
            requestId === this.saveState.sheetLoadRequestId &&
            sheetId === this.selectedSheetId
          ) {
            this.feedbackErrorMessage =
              error instanceof PreviousNextActionOutOfSyncError
                ? 'ネクストアクションは更新されましたが、表示が最新ではありません。画面を再読み込みしてください。'
                : 'ネクストアクションを更新できませんでした。時間を置いて再度お試しください。'
            this.snackBar.open(this.feedbackErrorMessage, '閉じる', {
              duration: 5000,
            })
          }
        },
      })
  }

  /** 結果入力が可能な本人または管理ロールかを返す。 */
  get canEditPreviousResults(): boolean {
    return this.isOperatorSelf || this.viewer.canManage
  }

  /** 結果ラジオの変更に合わせてその他の記述を切り替え、保存する。 */
  onPreviousResultChoiceChanged(
    nextActionId: string,
    choice: KpiNextActionResultChoice,
  ): void {
    const form = this.previousResultForm(nextActionId)
    // click は Angular の change によるフォーム更新より先に発火する。
    form.controls.choice.setValue(choice)
    this.savePreviousNextActionResult(nextActionId)
  }

  /** 指定した前回結果でその他が選択されているかを返す。 */
  isOtherResultChoice(nextActionId: string): boolean {
    return (
      this.previousResultForm(nextActionId).controls.choice.value ===
      KpiNextActionResultChoice.Other
    )
  }

  /** 未回答とその他の記述待ちは除外し、結果 1 件を自動保存キューへ積む。 */
  savePreviousNextActionResult(nextActionId: string): void {
    if (!this.canEditPreviousResults) {
      throw new Error('前回結果を編集する権限がありません')
    }
    const sheet = this.requirePersistedSheet()
    const persistedAction = sheet.previousSheet?.nextActions.find(
      ({ id }) => id === nextActionId,
    )
    if (persistedAction == null) {
      throw new Error(`Previous next action is missing: ${nextActionId}`)
    }
    const formValue = this.previousResultForm(nextActionId).getRawValue()
    const input = buildPreviousNextActionResultInput(
      nextActionId,
      formValue,
      persistedAction,
    )
    if (input == null) {
      // 未選択・記述待ちも null になるため、永続値と一致した場合だけ失敗を解除する。
      if (
        persistedAction.result?.choice === formValue.choice &&
        persistedAction.result.note ===
          (formValue.choice === KpiNextActionResultChoice.Other
            ? formValue.note
            : null)
      ) {
        this.clearFeedbackSaveFailure(
          sheet.id,
          null,
          'previousNextActionResult',
          nextActionId,
        )
      }
      return
    }
    this.enqueueFeedbackSave(
      sheet.id,
      () =>
        this.kpiDashboardService.updatePreviousNextActionResult(
          sheet.id,
          input,
        ),
      (persisted, action) =>
        applyPreviousNextActionResult(
          persisted,
          typeof action === 'undefined' ? input : action,
        ),
      null,
      'previousNextActionResult',
      nextActionId,
    )
  }

  /** 本人の確認後、先行する自動保存が成功してから記入完了を記録する。 */
  completeFeedbackSheetResponse(): void {
    if (!this.isOperatorSelf) {
      throw new Error('記入完了は対象オペレーター本人のみ操作できます')
    }
    if (
      this.isResponseAnswered ||
      this.isCompletingResponse ||
      this.isConfirmingResponse ||
      this.isChangingAssessments
    ) {
      return
    }
    // 保存済みの結果が無い行だけを対象にする（保存済みの行はヒントどおり旧値が残る）。
    const rowsWithoutPersistedResult = new Set(
      this.previousNextActionRows
        .filter(({ hasPersistedResult }) => !hasPersistedResult)
        .map(({ nextActionId }) => nextActionId),
    )
    if (
      [...this.previousResultForms.entries()].some(
        ([nextActionId, { controls }]) =>
          rowsWithoutPersistedResult.has(nextActionId) &&
          controls.choice.value === KpiNextActionResultChoice.Other &&
          controls.note.value.trim() === '',
      )
    ) {
      this.feedbackSaveErrorMessage =
        '「その他」の内容が未入力のネクストアクションがあります。内容を入力するか別の選択肢を選んでから記入完了を押してください。'
      return
    }
    const sheetId = this.requirePersistedSheet().id
    if (this.failedFeedbackSaves.get(sheetId)?.size) {
      this.feedbackSaveErrorMessage =
        '保存に失敗した項目があります。該当欄をもう一度編集して保存してから記入完了を押してください。'
      return
    }
    const requestId = this.saveState.sheetLoadRequestId
    this.isConfirmingResponse = true
    this.messageDialogService
      .showConfirm(
        '記入完了にすると、オペレーター一覧に「完了」として表示されます。一度記入完了にすると未記入には戻せません（記入内容は面談前まで編集できます）。',
        {
          title: '記入完了にしますか？',
          primaryButtonText: '記入完了',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (
          sheetId !== this.selectedSheetId ||
          requestId !== this.saveState.sheetLoadRequestId
        ) {
          return
        }
        this.isConfirmingResponse = false
        if (result !== DialogResult.PrimaryButtonClicked) {
          this.isCompletingResponse = false
          this.changeDetectorRef.markForCheck()
          return
        }
        this.enqueueFeedbackSave(
          sheetId,
          () =>
            defer(() => {
              if (this.failedFeedbackSaves.get(sheetId)?.size) {
                throw new UnresolvedFeedbackSaveError()
              }
              return this.kpiDashboardService.completeFeedbackSheetResponse(
                sheetId,
              )
            }).pipe(
              finalize(() => {
                if (
                  sheetId === this.selectedSheetId &&
                  requestId === this.saveState.sheetLoadRequestId
                ) {
                  this.isCompletingResponse = false
                  this.changeDetectorRef.markForCheck()
                }
              }),
            ),
          (sheet) => ({ ...sheet, responseStatus: KpiResponseStatus.Answered }),
          null,
          'responseStatus',
        )
      })
  }

  /** 指定した前回結果の編集フォームを返す。 */
  previousResultForm(nextActionId: string): PreviousResultForm {
    const form = this.previousResultForms.get(nextActionId)
    if (form == null) {
      throw new Error(`Previous result form is missing: ${nextActionId}`)
    }
    return form
  }

  /** 保存済みの値から表示行だけを作り直す。 */
  private rebuildPreviousNextActionRows(sheet: KpiFeedbackSheetView): void {
    this.previousNextActionRows = buildPreviousNextActionRows(
      sheet.previousSheet,
    )
  }

  /** 回答可能な確定行との差分だけを反映し、既存フォームと他行の下書きを維持する。 */
  private syncPreviousResultForms(sheet: KpiFeedbackSheetView): void {
    const rows = buildAnswerablePreviousNextActionRows(sheet)
    const answerableIds = new Set(rows.map(({ action }) => action.id))
    const previouslyClosedIds = new Set(
      this.previousNextActionRows
        .filter(({ isClosed }) => isClosed)
        .map(({ nextActionId }) => nextActionId),
    )
    for (const id of this.previousResultForms.keys()) {
      if (!answerableIds.has(id)) {
        this.previousResultForms.delete(id)
      }
    }
    for (const { action, value } of rows) {
      const existing = this.previousResultForms.get(action.id)
      if (existing != null) {
        // 再開への遷移時だけリセットし、再開後に入力した下書きは保持する。
        if (
          isPreviousNextActionReopened(action) &&
          previouslyClosedIds.has(action.id)
        ) {
          existing.controls.choice.reset(null)
        }
        continue
      }
      const state = resolveNextActionNoteState(value.choice, value.note)
      const form: PreviousResultForm = new FormGroup({
        choice: new FormControl<KpiNextActionResultChoice | null>(value.choice),
        note: new FormControl(
          { value: state.note, disabled: state.disabled },
          { nonNullable: true },
        ),
      })
      form.controls.choice.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((choice) => {
          const noteState = resolveNextActionNoteState(
            choice,
            form.controls.note.value,
          )
          form.controls.note.setValue(noteState.note, { emitEvent: false })
          if (noteState.disabled) {
            form.controls.note.disable({ emitEvent: false })
          } else {
            form.controls.note.enable({ emitEvent: false })
          }
        })
      this.previousResultForms.set(action.id, form)
    }
  }

  /** シート本体フィールドの blur 時に変更差分だけを保存キューへ積む。 */
  saveFeedbackSheetField(field: FeedbackSheetEditableField): void {
    this.assertCanEditFeedbackField(isManagerOnlySheetField(field))
    const persistedSheet = this.requirePersistedSheet()
    const input = buildFeedbackSheetUpdateInput(
      field,
      this.sheetForm.controls[field].value,
      persistedSheet,
    )
    if (input == null) {
      this.clearFeedbackSaveFailure(persistedSheet.id, null, field)
      return
    }

    this.enqueueFeedbackSave(
      persistedSheet.id,
      () =>
        this.kpiDashboardService.updateFeedbackSheet(persistedSheet.id, input),
      (sheet) => this.applyPersistedSheetInput(sheet, input),
      null,
      field,
    )
  }

  /** アセスメント記入フィールドの変更差分だけを保存キューへ積む。 */
  saveFeedbackAssessmentField(
    sheetAssessmentId: string,
    field: FeedbackAssessmentEditableField,
  ): void {
    this.assertCanEditFeedbackField(isManagerOnlyAssessmentField(field))
    const persistedSheet = this.requirePersistedSheet()
    const persistedAssessment =
      this.requirePersistedAssessment(sheetAssessmentId)
    const form = this.feedbackAssessmentForm(sheetAssessmentId)
    const input = isOperatorFeedbackField(field)
      ? buildOperatorFeedbackPointsUpdateInput(
          field,
          form.controls[field].getRawValue(),
          persistedAssessment,
        )
      : buildFeedbackAssessmentUpdateInput(
          field,
          form.controls[field].value,
          persistedAssessment,
        )
    if (input == null) {
      this.clearFeedbackSaveFailure(persistedSheet.id, sheetAssessmentId, field)
      return
    }

    this.enqueueFeedbackSave(
      persistedSheet.id,
      () =>
        this.kpiDashboardService.updateFeedbackSheetAssessment(
          sheetAssessmentId,
          input,
        ),
      (sheet) =>
        this.applyPersistedAssessmentInput(sheet, sheetAssessmentId, input),
      sheetAssessmentId,
      field,
    )
  }

  /**
   * ネクストアクション行の追加を禁止すべきかを返す。
   * 生成中は空行が草案マージ条件（空配列）を壊すため禁止するが、
   * ポーリング打ち切り後（stuck PENDING）は手動記入の逃げ道として解放する。
   */
  get isNextActionAddBlocked(): boolean {
    return this.isAiDraftGenerating
  }

  /** 空のネクストアクション入力欄を追加する。初回保存は入力後の blur で行う。 */
  addNextAction(sheetAssessmentId: string): void {
    this.assertCanEditManagerOnlyField()
    if (this.isNextActionAddBlocked) {
      return
    }
    const id = crypto.randomUUID()
    this.nextActionsFormArray(sheetAssessmentId).push(
      this.createNextActionForm({ id, text: '' }),
    )
  }

  /** 指定したネクストアクション入力欄を削除し、全量を直列保存する。 */
  removeNextAction(sheetAssessmentId: string, index: number): void {
    this.assertCanEditManagerOnlyField()
    const actions = this.nextActionsFormArray(sheetAssessmentId)
    if (index < 0 || index >= actions.length) {
      throw new Error(`Next action index is out of range: ${index}`)
    }
    actions.removeAt(index)
    this.saveNextActions(sheetAssessmentId)
  }

  /** ネクストアクションの blur 時に変更があれば全量を直列保存する。 */
  saveNextActions(sheetAssessmentId: string): void {
    this.assertCanEditManagerOnlyField()
    const persistedSheet = this.requirePersistedSheet()
    const persistedAssessment =
      this.requirePersistedAssessment(sheetAssessmentId)
    const actions = this.nextActionsFormArray(sheetAssessmentId).getRawValue()
    const input = buildNextActionsUpdateInput(actions, persistedAssessment)
    if (input == null) {
      this.clearFeedbackSaveFailure(
        persistedSheet.id,
        sheetAssessmentId,
        'nextActions',
      )
      return
    }

    this.enqueueFeedbackSave(
      persistedSheet.id,
      () =>
        this.kpiDashboardService.updateFeedbackSheetAssessment(
          sheetAssessmentId,
          input,
        ),
      (sheet) =>
        this.applyPersistedAssessmentInput(sheet, sheetAssessmentId, input),
      sheetAssessmentId,
      'nextActions',
    )
  }

  /** 指定アセスメントの編集フォームを返す。 */
  feedbackAssessmentForm(sheetAssessmentId: string): FeedbackAssessmentForm {
    const form = this.assessmentForms.get(sheetAssessmentId)
    if (form == null) {
      throw new Error(
        `Feedback assessment form is missing: ${sheetAssessmentId}`,
      )
    }
    return form
  }

  /** 指定アセスメントのネクストアクション FormArray を返す。 */
  nextActionsFormArray(sheetAssessmentId: string): FormArray<NextActionForm> {
    return this.feedbackAssessmentForm(sheetAssessmentId).controls.nextActions
  }

  /** 選択中シートが AI 草案生成中かを返す。 */
  get isAiDraftPending(): boolean {
    return this.selectedSheet?.aiDraftStatus === KpiAiDraftStatus.Pending
  }

  /** ポーリング打ち切り前の AI 草案生成中表示が必要かを返す。 */
  get isAiDraftGenerating(): boolean {
    return this.isAiDraftPending && !this.isAiDraftPollingTimedOut
  }

  /** 選択中シートの AI 草案生成が失敗したかを返す。 */
  get isAiDraftFailed(): boolean {
    return this.selectedSheet?.aiDraftStatus === KpiAiDraftStatus.Failed
  }

  /** 管理ロールに完了済み AI 草案の参照ナレッジを表示するかを返す。 */
  get showAiReferencedKnowledges(): boolean {
    return (
      this.viewer.canManage &&
      this.selectedSheet?.aiDraftStatus === KpiAiDraftStatus.Completed &&
      this.selectedSheet.aiReferencedKnowledges.length > 0
    )
  }

  /** 草案対象フィールドのフォーカス状態をポーリングマージへ伝える。 */
  setAiDraftFieldEditing(
    sheetAssessmentId: string | null,
    field: AiDraftEditableField,
    editing: boolean,
  ): void {
    const key = aiDraftEditingFieldKey(sheetAssessmentId, field)
    if (editing) {
      this.aiDraftEditingFields.add(key)
    } else {
      this.aiDraftEditingFields.delete(key)
    }
  }

  /** タイムアウト後に1回再取得し、生成中なら新しい5分枠でポーリングを再開する。 */
  reloadAiDraft(): void {
    const sheetId = this.selectedSheetId
    if (
      sheetId == null ||
      this.isAiDraftReloading ||
      this.isChangingAssessments
    ) {
      return
    }

    this.stopAiDraftPolling()
    this.isAiDraftPollingTimedOut = false
    this.isAiDraftReloading = true
    this.aiDraftReloadErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .feedbackSheet(sheetId)
      .pipe(
        finalize(() => {
          if (this.selectedSheetId === sheetId) {
            this.isAiDraftReloading = false
            this.changeDetectorRef.markForCheck()
          }
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (sheet) => {
          if (sheet.id !== this.selectedSheetId) {
            return
          }
          this.mergeAiDraftPollingResult(sheet)
          if (
            !this.isSheetLoading &&
            !this.isAiDraftPollingTimedOut &&
            sheet.aiDraftStatus === KpiAiDraftStatus.Pending
          ) {
            this.startAiDraftPolling()
          }
        },
        error: (error: unknown) => {
          if (this.recoverMissingSheet(error, sheetId)) return
          captureException(error)
          if (this.selectedSheetId === sheetId) {
            this.isAiDraftPollingTimedOut = true
            this.aiDraftReloadErrorMessage =
              'AI 草案の状態を再読み込みできませんでした。時間を置いて再度お試しください。'
          }
        },
      })
  }

  /** 回答済み状態かを返す。 */
  get isResponseAnswered(): boolean {
    return this.selectedSheet?.responseStatus === KpiResponseStatus.Answered
  }

  private load(
    sheetIdToSelect: string | null = null,
    isRecovery = false,
  ): void {
    let detailStarted = false
    const wasSticky = this.isFeedbackHeadSticky()
    this.isLoading = true
    this.feedbackErrorMessage = ''
    forkJoin({
      feedbackChoices: this.kpiDashboardService.feedbackChoices(),
      feedbackSheets: this.kpiDashboardService.feedbackSheets(
        this.dashboardId,
        this.operatorUserId,
      ),
    })
      .pipe(
        finalize(() => {
          if (isRecovery && !detailStarted) this.finishSheetRecovery()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: ({ feedbackChoices, feedbackSheets }) => {
          this.feedbackChoices = [...feedbackChoices].sort(
            (left, right) => left.sortOrder - right.sortOrder,
          )
          this.operatorGoodPointChoices = filterFeedbackChoices(
            this.feedbackChoices,
            KpiFeedbackChoiceCategory.GoodPoint,
          )
          this.operatorImprovementPointChoices = filterFeedbackChoices(
            this.feedbackChoices,
            KpiFeedbackChoiceCategory.ImprovementPoint,
          )
          this.feedbackSheets = orderFeedbackSheets(feedbackSheets)
          this.isLoading = false
          this.isSheetLoading = false
          this.changeDetectorRef.markForCheck()
          if (this.feedbackSheets.length === 0) {
            return
          }
          let sheetToSelect =
            sheetIdToSelect == null
              ? this.feedbackSheets.at(-1)
              : this.feedbackSheets.find(
                  (sheet) => sheet.id === sheetIdToSelect,
                )
          if (sheetToSelect == null && isRecovery) {
            sheetToSelect = this.feedbackSheets.at(-1)
          }
          if (sheetToSelect == null) {
            const error = new Error(
              `Feedback sheet to select is missing: ${sheetIdToSelect}`,
            )
            captureException(error)
            this.feedbackErrorMessage =
              '作成したフィードバックシートを表示できませんでした。再試行してください。'
            this.changeDetectorRef.markForCheck()
            return
          }
          detailStarted = true
          this.loadFeedbackSheet(sheetToSelect.id, wasSticky, '', isRecovery)
        },
        error: (error: unknown) => {
          if (!this.isDashboardNotFoundError(error)) captureException(error)
          this.isLoading = false
          this.isSheetLoading = false
          if (!handleDashboardNotFound(error, this.router, this.snackBar)) {
            this.feedbackErrorMessage =
              'フィードバックシートの読み込みに失敗しました。'
          }
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  /** 5秒間隔・最大5分の AI 草案ポーリングを開始する。 */
  private startAiDraftPolling(): void {
    const sheetId = this.selectedSheetId
    if (
      sheetId == null ||
      !this.isAiDraftPending ||
      this.saveState.isDestroyed
    ) {
      return
    }

    this.stopAiDraftPolling()
    const runId = this.aiDraftPollingRunId
    this.isAiDraftPollingTimedOut = false
    this.aiDraftReloadErrorMessage = ''
    interval(AI_DRAFT_POLLING_INTERVAL_MS)
      .pipe(
        exhaustMap(() =>
          this.kpiDashboardService.feedbackSheet(sheetId).pipe(
            catchError((error: unknown) => {
              if (!this.recoverMissingSheet(error, sheetId))
                captureException(error)
              return EMPTY
            }),
          ),
        ),
        takeUntil(timer(AI_DRAFT_POLLING_TIMEOUT_MS)),
        takeUntil(this.stopAiDraftPolling$),
        untilDestroyed(this),
        finalize(() => {
          if (
            runId === this.aiDraftPollingRunId &&
            this.selectedSheetId === sheetId &&
            this.isAiDraftPending &&
            !this.saveState.isDestroyed
          ) {
            this.isAiDraftPollingTimedOut = true
            this.changeDetectorRef.markForCheck()
          }
        }),
      )
      .subscribe((sheet) => {
        if (
          runId !== this.aiDraftPollingRunId ||
          sheet.id !== this.selectedSheetId
        ) {
          return
        }
        this.mergeAiDraftPollingResult(sheet)
      })
  }

  /** 現在のポーリングだけを停止し、後続の run と区別する。 */
  private stopAiDraftPolling(): void {
    this.aiDraftPollingRunId++
    this.stopAiDraftPolling$.next()
  }

  /** 最新詳細に残るブロックと維持欄を保存し、削除された対象の入力を破棄して読み直す。 */
  private reloadAfterAssessmentsChanged(
    sheetId: string,
    remoteSheet?: KpiFeedbackSheetView,
  ): void {
    if (this.isRecoveringChangedAssessments) return
    const requestId = this.saveState.sheetLoadRequestId
    this.isRecoveringChangedAssessments = true
    this.stopAiDraftPolling()
    this.isSheetLoading = true
    this.changeDetectorRef.markForCheck()
    const detailsReady = new ReplaySubject<boolean>(1)
    this.changedAssessmentDetailsReady$ = detailsReady
    const latestSheet =
      remoteSheet == null
        ? this.kpiDashboardService.feedbackSheet(sheetId)
        : of(remoteSheet)
    latestSheet
      .pipe(
        timeout({ first: FEEDBACK_SAVE_TIMEOUT_MS }),
        concatMap((sheet) => {
          if (
            requestId !== this.saveState.sheetLoadRequestId ||
            this.selectedSheetId !== sheetId
          )
            return EMPTY
          if (sheet == null || sheet.id !== sheetId)
            throw new Error(
              'KPI feedback sheet is missing during target recovery',
            )
          const preservation = getFeedbackAssessmentChangePreservation(
            this.requirePersistedSheet().assessments,
            sheet.assessments,
          )
          const { keptBlockIds, preservedFields } = preservation
          for (const save of this.saveState.pendingSaves) {
            if (
              save.sheetId === sheetId &&
              !preservedFields.has(save.affectedField) &&
              (save.affectedAssessmentId == null ||
                !keptBlockIds.has(save.affectedAssessmentId))
            ) {
              this.discardedFeedbackSaves.add(save)
            }
          }
          const failures = this.failedFeedbackSaves.get(sheetId)
          if (failures != null) {
            for (const key of failures) {
              if (
                ![...preservedFields].some((field) =>
                  key.startsWith(`null:${field}:`),
                ) &&
                ![...keptBlockIds].some((id) => key.startsWith(`${id}:`))
              )
                failures.delete(key)
            }
          }
          this.changedAssessmentDetailsReady$ = null
          detailsReady.next(true)
          detailsReady.complete()
          return this.flushFeedbackSavesBeforeChange(sheetId, preservation)
        }),
        finalize(() => {
          detailsReady.next(false)
          detailsReady.complete()
          this.changedAssessmentDetailsReady$ = null
          this.isRecoveringChangedAssessments = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          if (
            requestId !== this.saveState.sheetLoadRequestId ||
            this.selectedSheetId !== sheetId
          )
            return
          this.clearSelectedFeedbackSheet()
          this.isSheetLoading = false
          this.loadFeedbackSheet(sheetId)
          this.snackBar.open(
            '別の画面で対象アセスメントが変更された可能性があります。シートを読み込み直します。',
            '閉じる',
            { duration: 5000 },
          )
        },
        error: (error: unknown) => {
          if (this.recoverMissingSheet(error, sheetId)) return
          if (
            requestId !== this.saveState.sheetLoadRequestId ||
            this.selectedSheetId !== sheetId
          )
            return
          if (
            error instanceof UnresolvedFeedbackSaveError &&
            this.isRecoveringSheet
          )
            return
          if (handleDashboardNotFound(error, this.router, this.snackBar)) return
          if (
            !(error instanceof UnresolvedFeedbackSaveError) &&
            !this.isExpectedKpiError(error)
          )
            captureException(error)
          this.isSheetLoading = false
          this.isAiDraftPollingTimedOut = true
          this.feedbackErrorMessage =
            '対象アセスメントが別の画面で変更されました。保存できていない入力を確認してから再読み込みしてください。'
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  /** ポーリング結果からメタと安全に反映できる AI 草案だけを取り込む。 */
  private mergeAiDraftPollingResult(remoteSheet: KpiFeedbackSheetView): void {
    const confirmedSheet = this.saveState.confirmedPersistedSheet
    const persistedSheet = this.saveState.persistedSheet
    if (
      confirmedSheet == null ||
      persistedSheet == null ||
      confirmedSheet.id !== remoteSheet.id ||
      persistedSheet.id !== remoteSheet.id
    ) {
      return
    }
    if (
      confirmedSheet.assessments.length !== remoteSheet.assessments.length ||
      confirmedSheet.assessments.some(
        ({ sheetAssessmentId }) =>
          !remoteSheet.assessments.some(
            (assessment) => assessment.sheetAssessmentId === sheetAssessmentId,
          ),
      )
    ) {
      this.reloadAfterAssessmentsChanged(remoteSheet.id, remoteSheet)
      return
    }
    if (
      remoteSheet.aiDraftStatus === KpiAiDraftStatus.Pending &&
      confirmedSheet.aiDraftStatus === remoteSheet.aiDraftStatus &&
      confirmedSheet.isAiDraftStale === remoteSheet.isAiDraftStale &&
      confirmedSheet.responseStatus === remoteSheet.responseStatus &&
      confirmedSheet.updatedAt.getTime() === remoteSheet.updatedAt.getTime()
    ) {
      return
    }

    let mergedSheet: KpiFeedbackSheetView = {
      ...confirmedSheet,
      aiDraftStatus: remoteSheet.aiDraftStatus,
      isAiDraftStale: remoteSheet.isAiDraftStale,
      aiReferencedKnowledges: remoteSheet.aiReferencedKnowledges,
      responseStatus: remoteSheet.responseStatus,
      updatedAt: remoteSheet.updatedAt,
    }

    const sheetDraftFields: readonly AiDraftSheetField[] = [
      'managerHypothesis',
      'otherNotes',
    ]
    sheetDraftFields.forEach((field) => {
      if (
        !this.canApplyTextAiDraft(
          this.sheetForm.controls[field],
          persistedSheet[field],
          null,
          field,
        )
      ) {
        return
      }
      const remoteValue = remoteSheet[field]
      if (typeof remoteValue === 'undefined') {
        throw new Error(`AI draft sheet field is missing: ${field}`)
      }
      mergedSheet = { ...mergedSheet, [field]: remoteValue }
      this.sheetForm.controls[field].setValue(
        normalizeNullableText(remoteValue, 'AI draft sheet field is missing'),
        { emitEvent: false },
      )
    })

    mergedSheet = {
      ...mergedSheet,
      assessments: confirmedSheet.assessments.map((confirmedAssessment) => {
        const remoteAssessment = remoteSheet.assessments.find(
          ({ sheetAssessmentId }) =>
            sheetAssessmentId === confirmedAssessment.sheetAssessmentId,
        )
        const persistedAssessment = persistedSheet.assessments.find(
          ({ sheetAssessmentId }) =>
            sheetAssessmentId === confirmedAssessment.sheetAssessmentId,
        )
        if (remoteAssessment == null || persistedAssessment == null) {
          throw new Error(
            `AI draft assessment is missing: ${confirmedAssessment.sheetAssessmentId}`,
          )
        }
        return this.mergeAssessmentAiDraft(
          confirmedAssessment,
          persistedAssessment,
          remoteAssessment,
        )
      }),
    }

    this.saveState.confirmedPersistedSheet = mergedSheet
    this.rebuildPersistedSheet()
    this.selectedSheet = this.requirePersistedSheet()
    this.updateFeedbackSheetSummary(this.selectedSheet)
    this.initializeFeedbackAssessmentViewModels()
    if (remoteSheet.aiDraftStatus !== KpiAiDraftStatus.Pending) {
      this.isAiDraftPollingTimedOut = false
      this.stopAiDraftPolling()
    }
    this.changeDetectorRef.markForCheck()
  }

  /** 1アセスメント分の草案を、ローカルが安全な項目だけ確定値へ反映する。 */
  private mergeAssessmentAiDraft(
    confirmedAssessment: KpiFeedbackSheetAssessmentView,
    persistedAssessment: KpiFeedbackSheetAssessmentView,
    remoteAssessment: KpiFeedbackSheetAssessmentView,
  ): KpiFeedbackSheetAssessmentView {
    const sheetAssessmentId = confirmedAssessment.sheetAssessmentId
    const form = this.feedbackAssessmentForm(sheetAssessmentId)
    let mergedAssessment = confirmedAssessment
    const textFields: readonly Exclude<
      AiDraftAssessmentField,
      'nextActions'
    >[] = ['goodPoints', 'improvementPoints']
    textFields.forEach((field) => {
      if (
        !this.canApplyTextAiDraft(
          form.controls[field],
          persistedAssessment[field],
          sheetAssessmentId,
          field,
        )
      ) {
        return
      }
      const remoteValue = remoteAssessment[field]
      if (typeof remoteValue === 'undefined') {
        throw new Error(`AI draft assessment field is missing: ${field}`)
      }
      mergedAssessment = { ...mergedAssessment, [field]: remoteValue }
      form.controls[field].setValue(
        normalizeNullableText(
          remoteValue,
          'AI draft assessment field is missing',
        ),
        { emitEvent: false },
      )
    })

    if (
      this.canApplyNextActionsAiDraft(sheetAssessmentId, persistedAssessment)
    ) {
      mergedAssessment = {
        ...mergedAssessment,
        nextActions: remoteAssessment.nextActions,
      }
      form.controls.nextActions.clear({ emitEvent: false })
      remoteAssessment.nextActions.forEach(({ id, text }) => {
        form.controls.nextActions.push(
          this.createNextActionForm({ id, text }),
          {
            emitEvent: false,
          },
        )
      })
    }
    return mergedAssessment
  }

  /** ローカル・保存済み値が空で編集中でも保存中でもない場合だけ草案反映を許可する。 */
  private canApplyTextAiDraft(
    control: FormControl<string>,
    persistedValue: string | null | undefined,
    sheetAssessmentId: string | null,
    field: Exclude<AiDraftEditableField, 'nextActions'>,
  ): boolean {
    const localValue = control.value
    return (
      localValue === '' &&
      (persistedValue == null || persistedValue === '') &&
      !this.isAiDraftFieldBusy(sheetAssessmentId, field)
    )
  }

  /** ローカル・保存済みの双方が空で編集中でも保存中でもない場合だけ行動案を反映する。 */
  private canApplyNextActionsAiDraft(
    sheetAssessmentId: string,
    persistedAssessment: KpiFeedbackSheetAssessmentView,
  ): boolean {
    return (
      this.nextActionsFormArray(sheetAssessmentId).length === 0 &&
      persistedAssessment.nextActions.length === 0 &&
      !this.isAiDraftFieldBusy(sheetAssessmentId, 'nextActions')
    )
  }

  /** フォーカス中または同じ項目の保存が未完了なら草案反映を禁止する。 */
  private isAiDraftFieldBusy(
    sheetAssessmentId: string | null,
    field: AiDraftEditableField,
  ): boolean {
    if (
      this.aiDraftEditingFields.has(
        aiDraftEditingFieldKey(sheetAssessmentId, field),
      )
    ) {
      return true
    }
    return this.saveState.pendingSaves.some(
      (save) =>
        save.sheetId === this.selectedSheetId &&
        save.affectedAssessmentId === sheetAssessmentId &&
        save.affectedField === field,
    )
  }

  /** タブ上の生成・回答ステータスも詳細の最新メタへ揃える。 */
  private updateFeedbackSheetSummary(sheet: KpiFeedbackSheetView): void {
    this.feedbackSheets = this.feedbackSheets.map((summary) =>
      summary.id === sheet.id
        ? {
            ...summary,
            aiDraftStatus: sheet.aiDraftStatus,
            responseStatus: sheet.responseStatus,
          }
        : summary,
    )
  }

  /**
   * 自動保存要求を concatMap で直列実行するキューを初期化する。
   * destroy 時は Subject を complete し、投入済みの inner Observable が完了してから購読を終了する。
   */
  private initializeFeedbackSaveQueue(): void {
    this.saveState.queue
      .pipe(
        concatMap((save) => {
          this.activeFeedbackSaves.add(save)
          let succeeded = false
          return defer(() =>
            this.changedAssessmentDetailsReady$ == null
              ? of(true)
              : this.changedAssessmentDetailsReady$.pipe(take(1)),
          ).pipe(
            concatMap((ready) => {
              if (!ready) throw new UnresolvedFeedbackSaveError()
              if (this.discardedFeedbackSaves.has(save)) {
                this.completeFeedbackSave(save, false)
                return EMPTY
              }
              return save.operationFactory()
            }),
            tap((saved) => {
              if (this.discardedFeedbackSaves.has(save)) {
                this.completeFeedbackSave(save, false)
                return
              }
              if (!saved) {
                throw new Error('KPI feedback update returned false')
              }
              this.completeFeedbackSave(save, true, saved)
              succeeded = true
              this.failedFeedbackSaves
                .get(save.sheetId)
                ?.delete(save.failureKey)
              if (
                this.selectedSheetId === save.sheetId &&
                !this.failedFeedbackSaves.get(save.sheetId)?.size
              ) {
                this.feedbackSaveErrorMessage = ''
              }
            }),
            catchError((error: unknown) => {
              this.completeFeedbackSave(save, false)
              if (this.discardedFeedbackSaves.has(save)) return EMPTY
              if (
                save.loadRequestId !== this.saveState.sheetLoadRequestId &&
                this.isExpectedKpiError(error)
              )
                return EMPTY
              if (this.recoverMissingSheet(error, save.sheetId)) return EMPTY
              if (
                error instanceof KpiFeedbackTargetNotFoundError &&
                save.affectedAssessmentId != null &&
                this.selectedSheetId === save.sheetId &&
                save.loadRequestId === this.saveState.sheetLoadRequestId
              ) {
                this.reloadAfterAssessmentsChanged(save.sheetId)
                return EMPTY
              }
              if (!(error instanceof UnresolvedFeedbackSaveError)) {
                captureException(error)
              }
              if (
                save.affectedField !== 'responseStatus' &&
                save.loadRequestId === this.saveState.sheetLoadRequestId
              ) {
                let failures = this.failedFeedbackSaves.get(save.sheetId)
                if (failures == null) {
                  failures = new Set()
                  this.failedFeedbackSaves.set(save.sheetId, failures)
                }
                failures.add(save.failureKey)
              }
              if (
                this.selectedSheetId === save.sheetId &&
                save.loadRequestId === this.saveState.sheetLoadRequestId
              ) {
                this.feedbackSaveErrorMessage =
                  error instanceof CustomError &&
                  error.code === CustomErrorCode.FORBIDDEN
                    ? 'この欄を編集する権限がありません。画面を再読み込みして最新の権限で開き直してください。編集中の値は保存されていません。'
                    : error instanceof UnresolvedFeedbackSaveError
                      ? '保存に失敗した項目があります。該当欄をもう一度編集して保存してから記入完了を押してください。'
                      : save.affectedField === 'responseStatus'
                        ? '記入完了を記録できませんでした。時間を置いて再度お試しください。'
                        : '入力内容を保存できませんでした。編集中の値は保持されています。時間を置いて再度お試しください。'
              }
              return EMPTY
            }),
            finalize(() => {
              this.activeFeedbackSaves.delete(save)
              if (
                this.saveState.pendingSaveCount !==
                this.saveState.pendingSaves.length
              ) {
                captureException(
                  new Error('Feedback save queue count is invalid'),
                )
                this.saveState.pendingSaveCount =
                  this.saveState.pendingSaves.length
                this.feedbackSaveStatus = 'idle'
              } else if (this.selectedSheetId === save.sheetId) {
                this.feedbackSaveStatus =
                  this.saveState.pendingSaveCount > 0
                    ? 'saving'
                    : succeeded
                      ? 'saved'
                      : 'idle'
              }
              if (!this.saveState.isDestroyed) {
                this.changeDetectorRef.markForCheck()
              }
              this.feedbackSaveFinished$.next()
            }),
          )
        }),
      )
      .subscribe()
  }

  /** 差分のない項目の失敗を解除し、未保存変更が残っていなければエラー表示も消す。 */
  private clearFeedbackSaveFailure(
    sheetId: string,
    assessmentId: string | null,
    field: QueuedFeedbackSave['affectedField'],
    actionId: string | null = null,
  ): void {
    this.failedFeedbackSaves
      .get(sheetId)
      ?.delete(feedbackSaveFailureKey(assessmentId, field, actionId))
    if (!this.failedFeedbackSaves.get(sheetId)?.size) {
      this.feedbackSaveErrorMessage = ''
    }
  }

  /**
   * サービス呼び出しと保存済み値の反映処理を自動保存キューへ追加する。
   * 破棄後の blur は保存対象が既に画面から消えており、throw してもユーザーに復旧手段がないため無視する。
   */
  private enqueueFeedbackSave<Result>(
    sheetId: string,
    operationFactory: () => Observable<Result>,
    applyPersistedValue: (
      sheet: KpiFeedbackSheetView,
      result: Result | undefined,
    ) => KpiFeedbackSheetView,
    affectedAssessmentId: string | null,
    affectedField: QueuedFeedbackSave['affectedField'],
    affectedActionId: string | null = null,
  ): void {
    if (this.saveState.isDestroyed) {
      return
    }
    const save: QueuedFeedbackSave = {
      sheetId,
      loadRequestId: this.saveState.sheetLoadRequestId,
      operationFactory,
      failureKey: feedbackSaveFailureKey(
        affectedAssessmentId,
        affectedField,
        affectedActionId,
      ),
      // キューは戻り値型を消去するが、呼び出し側の factory とパッチは同じ型で結び付ける。
      applyPersistedValue: (sheet, result) =>
        applyPersistedValue(sheet, result as Result | undefined),
      affectedAssessmentId,
      affectedField,
    }
    this.saveState.pendingSaves.push(save)
    if (affectedField === 'responseStatus') {
      this.isCompletingResponse = true
    }
    this.saveState.pendingSaveCount = this.saveState.pendingSaves.length
    this.rebuildPersistedSheet(affectedAssessmentId)
    this.feedbackSaveStatus = 'saving'
    this.saveState.queue.next(save)
    this.changeDetectorRef.markForCheck()
  }

  /** 完了した保存を確定値へ反映または破棄し、未完了パッチから比較基準を再構築する。 */
  private completeFeedbackSave(
    save: QueuedFeedbackSave,
    succeeded: boolean,
    result?: unknown,
  ): void {
    const pendingIndex = this.saveState.pendingSaves.indexOf(save)
    if (pendingIndex === -1) {
      throw new Error('Pending feedback save is missing')
    }
    const confirmedSheet = this.saveState.confirmedPersistedSheet
    const affectsSelectedSheet =
      confirmedSheet?.id === save.sheetId &&
      save.loadRequestId === this.saveState.sheetLoadRequestId
    if (
      affectsSelectedSheet &&
      save.affectedField === 'responseStatus' &&
      this.discardedFeedbackSaves.has(save)
    ) {
      this.isCompletingResponse = false
    }
    if (succeeded && confirmedSheet != null && affectsSelectedSheet) {
      this.saveState.confirmedPersistedSheet = save.applyPersistedValue(
        confirmedSheet,
        result,
      )
    }
    this.saveState.pendingSaves.splice(pendingIndex, 1)
    this.saveState.pendingSaveCount = this.saveState.pendingSaves.length
    this.rebuildPersistedSheet(
      affectsSelectedSheet ? save.affectedAssessmentId : null,
    )
    if (
      succeeded &&
      affectsSelectedSheet &&
      (save.affectedField === 'previousNextActionResult' ||
        save.affectedField === 'nextActions' ||
        save.affectedField === 'responseStatus') &&
      this.saveState.confirmedPersistedSheet != null
    ) {
      this.selectedSheet = this.saveState.confirmedPersistedSheet
      this.syncPreviousResultForms(this.selectedSheet)
      this.rebuildPreviousNextActionRows(this.selectedSheet)
      this.updateFeedbackSheetSummary(this.selectedSheet)
    }
  }

  /** 確定済み値へ同一シート・同一読込世代の未完了パッチを順に重ねて差分比較基準を更新する。 */
  private rebuildPersistedSheet(
    affectedAssessmentId: string | null = null,
  ): void {
    const confirmedSheet = this.saveState.confirmedPersistedSheet
    if (confirmedSheet == null) {
      this.saveState.persistedSheet = null
      return
    }

    this.saveState.persistedSheet = this.saveState.pendingSaves
      .filter(
        ({ sheetId, loadRequestId }) =>
          sheetId === confirmedSheet.id &&
          loadRequestId === this.saveState.sheetLoadRequestId,
      )
      .reduce(
        (sheet, save) => save.applyPersistedValue(sheet, undefined),
        confirmedSheet,
      )
    if (affectedAssessmentId != null) {
      this.refreshFeedbackAssessmentViewModel(affectedAssessmentId)
    }
  }

  /** 非管理ロールが主任記入欄を操作した場合は保存前に明示エラーにする。 */
  private assertCanEditFeedbackField(managerOnly: boolean): void {
    if (managerOnly) {
      this.assertCanEditManagerOnlyField()
    }
  }

  /** 主任専用の操作は管理ロールのみ許可する。 */
  private assertCanEditManagerOnlyField(): void {
    if (!this.viewer.canManage) {
      throw new Error('主任記入欄は管理ロールのみ編集できます')
    }
  }

  /** 取得したシート詳細からシート本体とアセスメントの編集フォームを構築する。 */
  private initializeFeedbackForms(sheet: KpiFeedbackSheetView): void {
    this.sheetForm.setValue(
      {
        interviewDate: formatDateInputValue(sheet.interviewDate),
        consultationTopics: normalizeNullableText(
          sheet.consultationTopics,
          'Feedback sheet field is missing',
        ),
        operatorSelfIssues: normalizeNullableText(
          sheet.operatorSelfIssues,
          'Feedback sheet field is missing',
        ),
        managerHypothesis: normalizeNullableText(
          sheet.managerHypothesis,
          'Feedback sheet field is missing',
        ),
        otherNotes: normalizeNullableText(
          sheet.otherNotes,
          'Feedback sheet field is missing',
        ),
      },
      { emitEvent: false },
    )
    this.assessmentForms.clear()
    sheet.assessments.forEach((assessment) => {
      const form = new FormGroup({
        operatorGoodPoints: createOperatorFeedbackPointsForm(
          assessment.operatorGoodPoints,
          this.operatorGoodPointChoices,
        ),
        operatorImprovementPoints: createOperatorFeedbackPointsForm(
          assessment.operatorImprovementPoints,
          this.operatorImprovementPointChoices,
        ),
        goodPoints: new FormControl(
          normalizeNullableText(
            assessment.goodPoints,
            'Feedback sheet field is missing',
          ),
          { nonNullable: true },
        ),
        improvementPoints: new FormControl(
          normalizeNullableText(
            assessment.improvementPoints,
            'Feedback sheet field is missing',
          ),
          { nonNullable: true },
        ),
        nextActions: new FormArray(
          assessment.nextActions.map(({ id, text }) =>
            this.createNextActionForm({ id, text }),
          ),
        ),
      })
      this.assessmentForms.set(assessment.sheetAssessmentId, form)
    })
  }

  /** 読み込んだシートから CD 中に不変なアセスメント表示値を前計算する。 */
  private initializeFeedbackAssessmentViewModels(): void {
    this.feedbackAssessmentViewModels =
      this.requirePersistedSheet().assessments.map((assessment) =>
        this.buildFeedbackAssessmentViewModel(assessment),
      )
  }

  /** 自動保存で変化したアセスメントだけ表示モデルを再計算する。 */
  private refreshFeedbackAssessmentViewModel(sheetAssessmentId: string): void {
    if (this.feedbackAssessmentViewModels.length === 0) {
      return
    }
    const assessment = this.requirePersistedAssessment(sheetAssessmentId)
    const viewModelIndex = this.feedbackAssessmentViewModels.findIndex(
      ({ assessment: current }) =>
        current.sheetAssessmentId === sheetAssessmentId,
    )
    if (viewModelIndex === -1) {
      throw new Error(
        `Feedback assessment view model is missing: ${sheetAssessmentId}`,
      )
    }
    const viewModel = this.buildFeedbackAssessmentViewModel(assessment)
    this.feedbackAssessmentViewModels = [
      ...this.feedbackAssessmentViewModels.slice(0, viewModelIndex),
      viewModel,
      ...this.feedbackAssessmentViewModels.slice(viewModelIndex + 1),
    ]
  }

  /** アセスメント 1 件をテンプレート表示用モデルへ変換する。 */
  private buildFeedbackAssessmentViewModel(
    assessment: KpiFeedbackSheetAssessmentView,
  ): FeedbackAssessmentViewModel {
    const average = calculateKeyActionAverage(assessment)
    const meetingTitleText = formatFeedbackMeetingTitle(assessment.meetingTitle)
    return {
      assessment,
      form: this.feedbackAssessmentForm(assessment.sheetAssessmentId),
      assessDateText: formatFeedbackDate(assessment.assessDate),
      meetingTitleText,
      assessmentLinkParams: {
        'user-id': this.operatorUserId,
        'assessment-id': assessment.assessmentId,
      },
      assessmentLinkLabel: buildAssessmentLinkLabel(meetingTitleText),
      overallScoreText: formatFeedbackOverallScore(assessment.overallScore),
      keyActionAverageText: average == null ? '-' : `${average.toFixed(1)} / 5`,
      keyActionScoreRows: buildKeyActionScoreRows(assessment, this.keyActions),
      nextActionResultTexts: Object.fromEntries(
        assessment.nextActions.map((action) => [
          action.id,
          formatNextActionStatus(action),
        ]),
      ),
    }
  }

  /** ネクストアクション1件分の型付きフォームを構築する。 */
  private createNextActionForm(action: KpiNextActionInput): NextActionForm {
    return new FormGroup({
      id: new FormControl(action.id, { nonNullable: true }),
      text: new FormControl(action.text, { nonNullable: true }),
    })
  }

  /** 保存済みシート詳細を取得し、未取得ならエラーにする。 */
  private requirePersistedSheet(): KpiFeedbackSheetView {
    if (this.saveState.persistedSheet == null) {
      throw new Error('Persisted feedback sheet is missing')
    }
    return this.saveState.persistedSheet
  }

  /** 保存済みアセスメントを ID で取得し、未取得ならエラーにする。 */
  private requirePersistedAssessment(
    sheetAssessmentId: string,
  ): KpiFeedbackSheetAssessmentView {
    const assessment = this.requirePersistedSheet().assessments.find(
      (candidate) => candidate.sheetAssessmentId === sheetAssessmentId,
    )
    if (assessment == null) {
      throw new Error(
        `Persisted feedback assessment is missing: ${sheetAssessmentId}`,
      )
    }
    return assessment
  }

  /** シート本体 input を差分比較用スナップショットへ反映する。 */
  private applyPersistedSheetInput(
    sheet: KpiFeedbackSheetView,
    input: UpdateKpiFeedbackSheetInput,
  ): KpiFeedbackSheetView {
    return { ...sheet, ...input }
  }

  /** アセスメント input を差分比較用スナップショットへ反映する。 */
  private applyPersistedAssessmentInput(
    sheet: KpiFeedbackSheetView,
    sheetAssessmentId: string,
    input: UpdateKpiFeedbackSheetAssessmentInput,
  ): KpiFeedbackSheetView {
    const { nextActions, ...assessmentFields } = input
    return {
      ...sheet,
      assessments: sheet.assessments.map((assessment) => {
        if (assessment.sheetAssessmentId !== sheetAssessmentId) {
          return assessment
        }
        if (typeof nextActions === 'undefined') {
          return { ...assessment, ...assessmentFields }
        }
        if (nextActions == null) {
          throw new Error('Feedback next actions cannot be null')
        }
        return {
          ...assessment,
          ...assessmentFields,
          nextActions: nextActions.map((action) => {
            const persistedAction = assessment.nextActions.find(
              (candidate) => candidate.id === action.id,
            )
            return persistedAction == null
              ? { ...action, result: null, closedAt: null }
              : {
                  ...action,
                  result: persistedAction.result,
                  closedAt: persistedAction.closedAt,
                }
          }),
        }
      }),
    }
  }

  /** シート本体の消失だけを、コードと完全一致の文言で判定する。 */
  private isDeletedSheetError(error: unknown): boolean {
    return isKpiCustomError(error, CustomErrorCode.NOT_FOUND, [
      KPI_SHEET_NOT_FOUND_MESSAGE,
    ])
  }

  /** 前回シートまたはそのネクストアクションの変更・消失を判定する。 */
  private isPreviousSheetError(error: unknown): boolean {
    return (
      isKpiCustomError(error, CustomErrorCode.BAD_REQUEST, [
        KPI_PREVIOUS_SHEET_MESSAGES.sheetMissing,
        KPI_PREVIOUS_SHEET_MESSAGES.actionIdMissing,
      ]) ||
      isKpiCustomError(error, CustomErrorCode.NOT_FOUND, [
        KPI_PREVIOUS_SHEET_MESSAGES.actionMissing,
        KPI_SHEET_ASSESSMENT_NOT_FOUND_MESSAGE,
        KPI_NEXT_ACTION_NOT_FOUND_MESSAGE,
      ])
    )
  }

  /** 想定したシートの競合を回復し、呼び出し元へ処理済みかを返す。 */
  private recoverMissingSheet(error: unknown, sheetId: string): boolean {
    if (this.saveState.isDestroyed) return false
    if (this.isDeletedSheetError(error)) {
      return this.recoverStaleSheet(sheetId, 'sheet')
    }
    if (this.isPreviousSheetError(error)) {
      return this.recoverStaleSheet(sheetId, 'previous-sheet')
    }
    return false
  }

  /**
   * 一覧と詳細を再取得する簡易復旧。未保存入力は失われる場合がある。
   * 自動保存キューと整合する完全な復旧は後続 SBI で対応する。
   */
  private recoverStaleSheet(
    sheetId: string,
    kind: 'sheet' | 'previous-sheet',
  ): boolean {
    if (this.saveState.isDestroyed) return false
    if (this.isRecoveringSheet) return true
    this.isRecoveringSheet = true
    for (const save of this.saveState.pendingSaves) {
      if (
        save.sheetId === sheetId &&
        (kind === 'sheet' || save.affectedField === 'previousNextActionResult')
      )
        this.discardedFeedbackSaves.add(save)
    }
    const sheetIdToSelect =
      this.selectedSheetId == null ? sheetId : this.selectedSheetId
    this.clearSelectedFeedbackSheet()
    this.isSheetLoading = true
    this.snackBar.open(
      kind === 'sheet'
        ? 'このシートは削除されました。一覧を更新しました。'
        : '前回シートの内容が変更されたため、画面を更新しました。',
      '閉じる',
      { duration: 5000 },
    )
    this.changeDetectorRef.markForCheck()
    if (kind === 'previous-sheet' && this.saveState.pendingSaveCount > 0) {
      this.feedbackSaveFinished$
        .pipe(
          filter(() => this.saveState.pendingSaveCount === 0),
          take(1),
          timeout({ first: 10_000 }),
          catchError((error: unknown) => {
            if (error instanceof TimeoutError) return of(undefined)
            return throwError(() => error)
          }),
          untilDestroyed(this),
        )
        .subscribe(() => this.load(sheetIdToSelect, true))
    } else {
      this.load(sheetIdToSelect, true)
    }
    return true
  }

  /** 復旧要求の終了時に次の独立した復旧と再試行を許可する。 */
  private finishSheetRecovery(): void {
    this.isRecoveringSheet = false
    this.recoveryRetried = false
  }

  /** 既知の KPI 競合だけを通常の通信障害から区別する。 */
  private isExpectedKpiError(error: unknown): boolean {
    return (
      this.isDeletedSheetError(error) ||
      this.isPreviousSheetError(error) ||
      this.isDashboardNotFoundError(error) ||
      error instanceof KpiFeedbackTargetNotFoundError
    )
  }

  /** ダッシュボード・メンバーの消失だけを判定する。 */
  private isDashboardNotFoundError(error: unknown): boolean {
    return isKpiCustomError(error, CustomErrorCode.NOT_FOUND, [
      KPI_DASHBOARD_NOT_FOUND_MESSAGE,
      KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE,
    ])
  }

  /** シート一覧を再取得し、最新シートを選択する。 */
  private reloadFeedbackSheets(): void {
    this.feedbackErrorMessage = ''
    this.kpiDashboardService
      .feedbackSheets(this.dashboardId, this.operatorUserId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (sheets) => {
          this.feedbackSheets = orderFeedbackSheets(sheets)
          if (this.feedbackSheets.length === 0) {
            this.clearSelectedFeedbackSheet()
            this.changeDetectorRef.markForCheck()
            return
          }

          const latestSheet = this.feedbackSheets.at(-1)
          if (latestSheet == null) {
            throw new Error('Latest feedback sheet is missing')
          }
          this.clearSelectedFeedbackSheet()
          this.selectFeedbackSheet(latestSheet.id)
        },
        error: (error: unknown) => {
          if (!this.isDashboardNotFoundError(error)) captureException(error)
          if (!handleDashboardNotFound(error, this.router, this.snackBar)) {
            this.feedbackErrorMessage =
              'フィードバックシート一覧の更新に失敗しました。'
          }
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  /** 選択中シートと編集フォームを空状態へ戻す。 */
  private clearSelectedFeedbackSheet(): void {
    this.stopAiDraftPolling()
    this.saveState.sheetLoadRequestId++
    this.previousResultForms.clear()
    if (this.selectedSheetId != null) {
      this.failedFeedbackSaves.delete(this.selectedSheetId)
    }
    this.isCompletingResponse = false
    this.isConfirmingResponse = false
    this.selectedSheetId = null
    this.selectedSheet = null
    this.saveState.confirmedPersistedSheet = null
    this.saveState.persistedSheet = null
    this.previousNextActionRows = []
    this.feedbackAssessmentViewModels = []
    this.assessmentForms.clear()
    this.aiDraftEditingFields.clear()
    this.isAiDraftPollingTimedOut = false
    this.isAiDraftReloading = false
    this.aiDraftReloadErrorMessage = ''
  }

  /** 選択中シートの論理削除 Mutation を実行する。 */
  private executeFeedbackSheetDelete(sheetId: string): void {
    this.isDeletingSheet = true
    this.feedbackErrorMessage = ''
    this.kpiDashboardService
      .deleteFeedbackSheet(sheetId)
      .pipe(
        finalize(() => {
          this.isDeletingSheet = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (deleted) => {
          if (!deleted) {
            const error = new Error('deleteKpiFeedbackSheet returned false')
            captureException(error)
            this.feedbackErrorMessage =
              'フィードバックシートを削除できませんでした。時間を置いて再度お試しください。'
            return
          }
          this.snackBar.open('フィードバックシートを削除しました。', '閉じる', {
            duration: 3000,
          })
          this.reloadFeedbackSheets()
        },
        error: (error: unknown) => {
          if (this.recoverMissingSheet(error, sheetId)) return
          captureException(error)
          this.feedbackErrorMessage =
            'フィードバックシートを削除できませんでした。時間を置いて再度お試しください。'
        },
      })
  }
}

/** 編集対象がオペレーター記入欄かを判定する。 */
function isOperatorFeedbackField(
  field: FeedbackAssessmentEditableField,
): field is OperatorFeedbackAssessmentEditableField {
  return field === 'operatorGoodPoints' || field === 'operatorImprovementPoints'
}

/** 保存済み値と有効な選択肢からオペレーター記入フォームを構築する。 */
function createOperatorFeedbackPointsForm(
  persisted: KpiOperatorFeedbackPoints | null | undefined,
  choices: KpiFeedbackChoices,
): OperatorFeedbackPointsForm {
  if (typeof persisted === 'undefined') {
    throw new Error('Feedback operator points are missing')
  }
  const selectedChoiceIds = new Set(
    persisted == null ? [] : persisted.choiceIds,
  )
  const value: EditableFeedbackPointsValue = {
    choices: choices.map((choice) => ({
      choiceId: choice.id,
      selected: selectedChoiceIds.has(choice.id),
    })),
    freeText: persisted == null ? '' : persisted.freeText,
  }
  return new FormGroup({
    choices: new FormArray(
      value.choices.map(
        (choice) =>
          new FormGroup({
            choiceId: new FormControl(choice.choiceId, { nonNullable: true }),
            selected: new FormControl(choice.selected, { nonNullable: true }),
          }),
      ),
    ),
    freeText: new FormControl(value.freeText, { nonNullable: true }),
  })
}

/** フィードバックシート一覧を回番号の昇順に整列する。 */
function orderFeedbackSheets(sheets: KpiFeedbackSheets): KpiFeedbackSheets {
  return [...sheets].sort((left, right) => left.sheetNumber - right.sheetNumber)
}

/** シート本体とアセスメントの草案編集状態を一意に識別するキーを返す。 */
function aiDraftEditingFieldKey(
  sheetAssessmentId: string | null,
  field: AiDraftEditableField,
): string {
  return sheetAssessmentId == null
    ? `sheet:${field}`
    : `assessment:${sheetAssessmentId}:${field}`
}

/** サーバー更新後の前回アクションとローカル表示の不整合。 */
class PreviousNextActionOutOfSyncError extends Error {}

/** 先行保存の失敗が未解決であることを示す。 */
class UnresolvedFeedbackSaveError extends Error {}

/** シートの他の項目を維持したまま、指定した前回アクションの状態を差し替える。 */
function applyPreviousNextActionClosed(
  sheet: KpiFeedbackSheetView,
  row: PreviousNextActionRow,
  closedAt: Date | null,
): KpiFeedbackSheetView {
  const previousSheet = sheet.previousSheet
  if (previousSheet == null) {
    throw new PreviousNextActionOutOfSyncError(
      'Previous feedback sheet is missing',
    )
  }
  const index = previousSheet.nextActions.findIndex(
    (action) =>
      action.id === row.nextActionId &&
      action.sheetAssessmentId === row.sheetAssessmentId,
  )
  if (index === -1) {
    throw new PreviousNextActionOutOfSyncError(
      `Previous next action is missing: ${row.nextActionId}`,
    )
  }
  return {
    ...sheet,
    previousSheet: {
      ...previousSheet,
      nextActions: previousSheet.nextActions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, closedAt } : action,
      ),
    },
  }
}

/** 保存失敗をシート内のアセスメント・項目・アクション単位で識別する。 */
function feedbackSaveFailureKey(
  assessmentId: string | null,
  field: QueuedFeedbackSave['affectedField'],
  actionId: string | null,
): string {
  return `${assessmentId}:${field}:${actionId}`
}
